import * as React from "react"
import { Box, CircularProgress, Tab, Tabs, SxProps, Theme } from "@mui/material"

export interface LivisTabItem {
  value: string | number
  label: React.ReactNode
  disabled?: boolean
  hidden?: boolean
  loading?: boolean
  trailingAction?: React.ReactNode
  id?: string
  className?: string
}

export interface LivisTabsProps {
  tabs: LivisTabItem[]
  value: string | number | false
  onChange: (event: React.SyntheticEvent, newValue: string | number) => void
  size?: "default" | "compact"
  variant?: "standard" | "scrollable" | "fullWidth"
  scrollButtons?: boolean | "auto"
  allowScrollButtonsMobile?: boolean
  endAdornment?: React.ReactNode
  ariaLabel?: string
  className?: string
  id?: string
  sx?: SxProps<Theme>
}

const ICON_SLOT_SIZE = 16

const getTabsSx = (size: "default" | "compact", variant: "standard" | "scrollable" | "fullWidth"): SxProps<Theme> => ({
  minHeight: size === "compact" ? "40px" : "44px",
  // fullWidth tab bars need the <Tabs> root itself to grow inside the
  // (flex, row) wrapper Box — otherwise it stays sized to its own content
  // and there's no extra row width left for the tabs to stretch into.
  ...(variant === "fullWidth" ? { flex: 1, width: "100%" } : {}),
  "& .MuiTabs-flexContainer": {
    flexWrap: "nowrap",
  },
  "& .MuiTabs-scroller": {
    overflowX: "auto !important",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  "& .MuiTab-root": {
    // fullWidth needs each tab to actually grow and share the row equally —
    // the flex:"0 0 auto"/minWidth:"max-content" used for standard/scrollable
    // tab bars (sized to their own label) would otherwise override MUI's
    // built-in .MuiTab-fullWidth stretch behavior.
    ...(variant === "fullWidth" ? { flex: 1, minWidth: 0 } : { flex: "0 0 auto", minWidth: "max-content" }),
    textTransform: "unset",
    fontFamily: '"Noto Sans Regular"',
    color: "var(--livis-gray-dark-font-color)",
    fontSize: size === "compact" ? "0.85rem" : "var(--livis-font-size-xs1)",
    minHeight: size === "compact" ? "40px" : "44px",
    padding: size === "compact" ? "6px 8px" : "6px 12px",
  },
  "& .MuiTab-iconWrapper": {
    marginBottom: 0,
    marginRight: "4px",
  },
  "& .Mui-selected": {
    color: "var(--livis-color-primary-dblue) !important",
    fontFamily: '"Noto Sans SemiBold" !important',
    borderBottom: "2px solid var(--livis-color-primary-dblue) !important",
    // A translucent mix (not a flat token) so the highlight stays visible against
    // whatever surface sits behind the tab bar on a given page — a flat color can
    // coincidentally match a page's ambient background and become invisible.
    backgroundColor: "color-mix(in srgb, var(--livis-color-primary-dblue) 12%, transparent) !important",
    borderRadius: "0.5rem 0.5rem 0 0",
  },
  "& .Mui-disabled": {
    color: "var(--livis-gray-font-color) !important",
    cursor: "not-allowed",
    opacity: 0.6,
    pointerEvents: "auto !important",
  },
  "@media (min-width: 2560px)": {
    "& .MuiTab-root": {
      fontSize: size === "compact" ? "0.95rem" : "1rem",
      padding: "0 1rem",
      minHeight: "48px",
    },
  },
})

/**
 * LivisTabs
 * Single generalized wrapper around MUI Tabs/Tab used everywhere in the app so
 * tab bars look and behave identically (border-radius, selected/hover/disabled
 * treatment, responsive scroll behavior from mobile through 4K).
 *
 * Renders only the tab bar (+ an optional endAdornment slot). Panel content,
 * permission filtering, and menu logic stay owned by the caller.
 */
export const LivisTabs: React.FC<LivisTabsProps> = ({
  tabs,
  value,
  onChange,
  size = "default",
  variant = "standard",
  scrollButtons = false,
  allowScrollButtonsMobile,
  endAdornment,
  ariaLabel,
  className = "",
  id,
  sx,
}) => {
  const visibleTabs = tabs.filter((item) => !item.hidden)

  return (
    <Box
      className={`livis-tabs-bar ${className}`.trim()}
      sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", minWidth: 0 }}
    >
      <Tabs
        id={id}
        value={value}
        onChange={onChange}
        aria-label={ariaLabel}
        variant={variant}
        scrollButtons={scrollButtons}
        allowScrollButtonsMobile={allowScrollButtonsMobile}
        sx={[getTabsSx(size, variant), ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      >
        {visibleTabs.map((item) => {
          const declaresLoading = item.loading !== undefined

          return (
            <Tab
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              id={item.id}
              className={item.className}
              iconPosition={declaresLoading ? "start" : undefined}
              icon={
                declaresLoading ? (
                  <Box
                    sx={{
                      width: ICON_SLOT_SIZE,
                      height: ICON_SLOT_SIZE,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.loading && (
                      <CircularProgress size={12} thickness={5} sx={{ color: "inherit" }} />
                    )}
                  </Box>
                ) : undefined
              }
              label={
                item.trailingAction ? (
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    <Box component="span">{item.label}</Box>
                    {item.trailingAction}
                  </Box>
                ) : (
                  item.label
                )
              }
            />
          )
        })}
      </Tabs>
      {endAdornment}
    </Box>
  )
}
